import React, { useState, useEffect, useRef, useCallback } from 'react';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  AlertTitle,
  Grid,
  TextField,
  MenuItem,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  Tooltip
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FileDownload as FileDownloadIcon,
  Inventory2 as InventoryIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import categoryQuantitiesReportService, {
  CategoryOption,
  ProductVariantOption,
  WarehouseOption,
  WarehouseQuantityRow,
  WarehouseQuantityDetail,
  PriceType
} from '../../../services/categoryQuantitiesReportService';

const PAGE_ID = 'category-quantities-report';

// أنواع الأسعار المتاحة لحساب قيمة المخزون (اختياري)
const PRICE_TYPES: { value: PriceType; label: string }[] = [
  { value: '', label: 'بدون (الكميات فقط)' },
  { value: 'retailPrice', label: 'سعر التجزئة' },
  { value: 'wholesalePrice', label: 'سعر الجملة' },
  { value: 'customPrice1', label: 'السعر المخصص 1' },
  { value: 'customPrice2', label: 'السعر المخصص 2' },
  { value: 'lastCost', label: 'آخر تكلفة' },
  { value: 'averageCost', label: 'متوسط التكلفة' }
];

// تنسيق الأرقام المالية
const formatMoney = (value: number): string =>
  (Number.isFinite(value) ? value : 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

// مفاتيح لا تُعتبر خصائص متغير حقيقية (الباركود/SKU يُعرضان في أعمدة منفصلة)
const IGNORED_ATTRIBUTE_KEYS = ['barcode', 'bar code', 'sku', 'الباركود', 'باركود', 'رمز'];

// استخراج نص المتغير من الخصائص (مع استثناء الباركود/ SKU)
const getVariantText = (attributes?: Record<string, string>): string => {
  if (!attributes) return '';
  return Object.entries(attributes)
    .filter(([key, value]) => {
      if (!value) return false;
      const normalizedKey = String(key).trim().toLowerCase();
      return !IGNORED_ATTRIBUTE_KEYS.includes(normalizedKey);
    })
    .map(([, value]) => value)
    .join(' - ');
};

// نص عرض خيار المنتج/المتغير
const getOptionLabel = (option: ProductVariantOption): string => {
  const parts: string[] = [option.productName];
  const variantText = getVariantText(option.attributes);
  if (variantText) parts.push(variantText);
  if (option.barcode && option.barcode !== 'غير متوفر') parts.push(`(${option.barcode})`);
  return parts.join(' - ');
};

const CategoryQuantitiesReport: React.FC = () => {
  const { hasPermission } = usePermissions();

  const hasViewPermission = hasPermission('reports', PAGE_ID, 'view_page');
  const hasExportPermission = hasPermission('reports', PAGE_ID, 'export_excel');

  // حالات الفلاتر
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [productOptions, setProductOptions] = useState<ProductVariantOption[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<ProductVariantOption[]>([]);
  const [productInput, setProductInput] = useState<string>('');
  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState<WarehouseOption[]>([]);

  // حالات النتائج
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingProducts, setLoadingProducts] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [results, setResults] = useState<WarehouseQuantityRow[]>([]);
  const [grandTotal, setGrandTotal] = useState<number>(0);

  // نوع السعر المختار لحساب قيمة المخزون (اختياري - فارغ = كميات فقط)
  const [priceType, setPriceType] = useState<PriceType>('');

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // جلب التصنيفات والمستودعات عند التحميل
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await categoryQuantitiesReportService.getCategories();
        if (response && response.success && Array.isArray(response.data)) {
          setCategories(response.data);
        }
      } catch (err) {
        console.error('فشل جلب التصنيفات:', err);
      }

      try {
        const whResponse = await categoryQuantitiesReportService.getWarehouses();
        if (whResponse && whResponse.success && Array.isArray(whResponse.data)) {
          setWarehouses(whResponse.data);
        }
      } catch (err) {
        console.error('فشل جلب المستودعات:', err);
      }
    };
    loadInitialData();
  }, []);

  // جلب المنتجات/المتغيرات عند تغيير التصنيف أو نص البحث (مع تأخير)
  useEffect(() => {
    if (!hasViewPermission) return;

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(async () => {
      try {
        setLoadingProducts(true);
        const response = await categoryQuantitiesReportService.getProducts(selectedCategory, productInput);
        if (response && response.success && Array.isArray(response.data)) {
          setProductOptions(response.data);
        } else {
          setProductOptions([]);
        }
      } catch (err) {
        console.error('فشل جلب المنتجات:', err);
        setProductOptions([]);
      } finally {
        setLoadingProducts(false);
      }
    }, 400);

    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, [selectedCategory, productInput, hasViewPermission]);

  // زر البحث
  const handleSearch = useCallback(async () => {
    // إذا لم يُحدد منتج ولم يُحدد تصنيف
    if (selectedProducts.length === 0 && !selectedCategory) {
      setError('الرجاء اختيار تصنيف أو منتج واحد على الأقل قبل البحث.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const variantIds = selectedProducts.map(p => p.variantId);
      const warehouseIds = selectedWarehouses.map(w => w._id);
      const response = await categoryQuantitiesReportService.getQuantities(
        variantIds,
        variantIds.length === 0 ? selectedCategory : '',
        warehouseIds
      );

      if (response && response.success && response.data) {
        setResults(response.data.warehouses || []);
        setGrandTotal(response.data.grandTotal || 0);
        setHasSearched(true);
      } else {
        setResults([]);
        setGrandTotal(0);
        setHasSearched(true);
      }
    } catch (err: any) {
      console.error('فشل جلب الكميات:', err);
      setError('حدث خطأ أثناء جلب كميات المستودعات. الرجاء المحاولة مرة أخرى.');
      setResults([]);
      setGrandTotal(0);
    } finally {
      setLoading(false);
    }
  }, [selectedProducts, selectedCategory, selectedWarehouses]);

  // مسح الفلاتر
  const handleClear = useCallback(() => {
    setSelectedCategory('');
    setSelectedProducts([]);
    setProductInput('');
    setSelectedWarehouses([]);
    setResults([]);
    setGrandTotal(0);
    setHasSearched(false);
    setError(null);
    setPriceType('');
  }, []);

  // ===== حسابات السعر والقيمة =====
  // سعر الوحدة حسب نوع السعر المختار
  const getUnitPrice = (detail: WarehouseQuantityDetail, type: PriceType): number => {
    if (!type) return 0;
    const value = (detail as any)[type];
    return typeof value === 'number' ? value : 0;
  };

  // قيمة تفصيل واحد = الكمية × سعر الوحدة
  const getDetailValue = (detail: WarehouseQuantityDetail, type: PriceType): number =>
    (Number(detail.quantity) || 0) * getUnitPrice(detail, type);

  // إجمالي قيمة مستودع = مجموع قيم تفاصيله
  const getWarehouseValue = (row: WarehouseQuantityRow, type: PriceType): number =>
    row.details.reduce((sum, detail) => sum + getDetailValue(detail, type), 0);

  // تسمية نوع السعر لعرضها في العناوين
  const priceTypeLabel = PRICE_TYPES.find(p => p.value === priceType)?.label || '';
  const valueColumnLabel = priceType ? `إجمالي القيمة (${priceTypeLabel})` : '';

  // إجمالي القيمة العام لكل المستودعات
  const grandValue = priceType
    ? results.reduce((sum, row) => sum + getWarehouseValue(row, priceType), 0)
    : 0;

  // بناء بيانات تفاصيل المستودع للتصدير
  const buildDetailsRows = (row: WarehouseQuantityRow) => {
    return row.details.map((detail, index) => {
      const base: Record<string, any> = {
        '#': index + 1,
        'المنتج': detail.productName,
        'المتغير': getVariantText(detail.attributes) || 'غير محدد',
        'الباركود': detail.barcode || '-',
        'SKU': detail.sku || '-',
        'الوحدة': detail.unitName || '-',
        'الكمية': detail.quantity
      };
      if (priceType) {
        base['سعر الوحدة'] = getUnitPrice(detail, priceType);
        base[valueColumnLabel] = getDetailValue(detail, priceType);
      }
      return base;
    });
  };

  const autoFitColumns = (ws: XLSX.WorkSheet, data: any[]) => {
    if (!ws['!cols']) ws['!cols'] = [];
    const colCount = Object.keys(data[0] || {}).length || 1;
    for (let i = 0; i < colCount; i++) {
      ws['!cols'][i] = { wch: 18 };
    }
  };

  // تصدير Excel لمستودع واحد
  const handleExportWarehouse = useCallback((row: WarehouseQuantityRow) => {
    if (!hasExportPermission) {
      setError('ليس لديك صلاحية لتصدير التقارير');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();

      // ورقة الملخص
      const summaryItem: Record<string, any> = {
        'المستودع': row.warehouseName,
        'الكمية الإجمالية': row.totalQuantity,
        'عدد المنتجات': row.details.length
      };
      if (priceType) {
        summaryItem[`إجمالي القيمة (${priceTypeLabel})`] = getWarehouseValue(row, priceType);
      }
      summaryItem['تاريخ التصدير'] = new Date().toLocaleString('ar-SA');
      const summaryData = [summaryItem];
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      autoFitColumns(wsSummary, summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'الملخص');

      // ورقة التفاصيل
      const detailsData = buildDetailsRows(row);
      const wsDetails = XLSX.utils.json_to_sheet(detailsData.length > 0 ? detailsData : [{ 'ملاحظة': 'لا توجد تفاصيل' }]);
      autoFitColumns(wsDetails, detailsData.length > 0 ? detailsData : [{}]);
      XLSX.utils.book_append_sheet(wb, wsDetails, 'التفاصيل');

      const safeName = row.warehouseName.replace(/[\\/:*?"<>|]/g, '_');
      const fileName = `تفاصيل_${safeName}_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('خطأ في تصدير Excel:', err);
      setError('حدث خطأ أثناء تصدير ملف Excel');
    }
  }, [hasExportPermission, priceType, priceTypeLabel]);

  // تصدير كل المستودعات في ملف واحد
  const handleExportAll = useCallback(() => {
    if (!hasExportPermission) {
      setError('ليس لديك صلاحية لتصدير التقارير');
      return;
    }

    if (results.length === 0) {
      setError('لا توجد بيانات للتصدير');
      return;
    }

    try {
      const wb = XLSX.utils.book_new();

      // ورقة ملخص كل المستودعات
      const summaryData = results.map(row => {
        const item: Record<string, any> = {
          'المستودع': row.warehouseName,
          'الكمية الإجمالية': row.totalQuantity,
          'عدد المنتجات': row.details.length
        };
        if (priceType) {
          item[`إجمالي القيمة (${priceTypeLabel})`] = getWarehouseValue(row, priceType);
        }
        return item;
      });
      const grandSummaryRow: Record<string, any> = {
        'المستودع': 'الإجمالي العام',
        'الكمية الإجمالية': grandTotal,
        'عدد المنتجات': 0
      };
      if (priceType) {
        grandSummaryRow[`إجمالي القيمة (${priceTypeLabel})`] = grandValue;
      }
      summaryData.push(grandSummaryRow);
      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      autoFitColumns(wsSummary, summaryData);
      XLSX.utils.book_append_sheet(wb, wsSummary, 'ملخص المستودعات');

      // ورقة تفاصيل شاملة لكل المستودعات
      const allDetails: any[] = [];
      results.forEach(row => {
        row.details.forEach(detail => {
          const item: Record<string, any> = {
            'المستودع': row.warehouseName,
            'المنتج': detail.productName,
            'المتغير': getVariantText(detail.attributes) || 'غير محدد',
            'الباركود': detail.barcode || '-',
            'SKU': detail.sku || '-',
            'الوحدة': detail.unitName || '-',
            'الكمية': detail.quantity
          };
          if (priceType) {
            item['سعر الوحدة'] = getUnitPrice(detail, priceType);
            item[valueColumnLabel] = getDetailValue(detail, priceType);
          }
          allDetails.push(item);
        });
      });
      const wsDetails = XLSX.utils.json_to_sheet(allDetails);
      autoFitColumns(wsDetails, allDetails);
      XLSX.utils.book_append_sheet(wb, wsDetails, 'التفاصيل');

      // ورقة ثالثة: إجمالي كميات المنتجات مجمّعة من كل المستودعات
      const productTotalsMap = new Map<string, {
        productName: string;
        variant: string;
        barcode: string;
        sku: string;
        unitName: string;
        totalQuantity: number;
        totalValue: number;
      }>();

      results.forEach(row => {
        row.details.forEach(detail => {
          const variantText = getVariantText(detail.attributes) || 'غير محدد';
          const key = [
            detail.productName || '',
            variantText,
            detail.barcode || '',
            detail.sku || '',
            detail.unitName || ''
          ].join('|');

          const value = priceType ? getDetailValue(detail, priceType) : 0;
          const existing = productTotalsMap.get(key);
          if (existing) {
            existing.totalQuantity += detail.quantity;
            existing.totalValue += value;
          } else {
            productTotalsMap.set(key, {
              productName: detail.productName,
              variant: variantText,
              barcode: detail.barcode || '-',
              sku: detail.sku || '-',
              unitName: detail.unitName || '-',
              totalQuantity: detail.quantity,
              totalValue: value
            });
          }
        });
      });

      const productTotalsData: Record<string, any>[] = Array.from(productTotalsMap.values()).map((item, index) => {
        const rowData: Record<string, any> = {
          '#': index + 1,
          'المنتج': item.productName,
          'المتغير': item.variant,
          'الباركود': item.barcode,
          'SKU': item.sku,
          'الوحدة': item.unitName,
          'إجمالي الكمية': item.totalQuantity
        };
        if (priceType) {
          rowData[valueColumnLabel] = item.totalValue;
        }
        return rowData;
      });

      // إضافة سطر الإجمالي العام في نهاية ورقة المنتجات
      if (productTotalsData.length > 0) {
        const grandRow: Record<string, any> = {
          '#': '',
          'المنتج': 'الإجمالي العام',
          'المتغير': '',
          'الباركود': '',
          'SKU': '',
          'الوحدة': '',
          'إجمالي الكمية': grandTotal
        };
        if (priceType) {
          grandRow[valueColumnLabel] = grandValue;
        }
        productTotalsData.push(grandRow);
      }

      const wsProductTotals = XLSX.utils.json_to_sheet(
        productTotalsData.length > 0 ? productTotalsData : [{ 'ملاحظة': 'لا توجد بيانات' }]
      );
      autoFitColumns(wsProductTotals, productTotalsData.length > 0 ? productTotalsData : [{}]);
      XLSX.utils.book_append_sheet(wb, wsProductTotals, 'اجمالي كميات المنتجات');

      const fileName = `عرض_كميات_التصنيفات_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('خطأ في تصدير Excel:', err);
      setError('حدث خطأ أثناء تصدير ملف Excel');
    }
  }, [hasExportPermission, results, grandTotal, priceType, priceTypeLabel, grandValue]);

  // عرض رسالة عدم الصلاحية
  if (!hasViewPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <AlertTitle>غير مصرح</AlertTitle>
          ليس لديك صلاحية للوصول إلى هذه الصفحة
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* العنوان */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          عرض كميات التصنيفات
        </Typography>
        <Button
          variant="outlined"
          startIcon={<FileDownloadIcon />}
          onClick={handleExportAll}
          disabled={!hasExportPermission || results.length === 0}
        >
          تصدير الكل
        </Button>
      </Box>

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* الفلاتر */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          {/* التصنيف */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="التصنيف"
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setProductInput('');
              }}
              fullWidth
              size="small"
            >
              <MenuItem value="">الكل</MenuItem>
              {categories.map((category) => (
                <MenuItem key={category._id} value={category._id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* المستودعات - اختيار متعدد */}
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              multiple
              options={warehouses}
              value={selectedWarehouses}
              onChange={(event, newValue) => setSelectedWarehouses(newValue)}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option._id === value._id}
              filterSelectedOptions
              noOptionsText="لا توجد مستودعات"
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip {...getTagProps({ index })} key={option._id} label={option.name} size="small" />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="المستودعات (يمكن اختيار أكثر من مستودع)"
                  placeholder="اختر المستودعات..."
                  size="small"
                />
              )}
            />
          </Grid>

          {/* المنتج / المتغير - اختيار متعدد */}
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              multiple
              options={productOptions}
              value={selectedProducts}
              loading={loadingProducts}
              onChange={(event, newValue) => setSelectedProducts(newValue)}
              inputValue={productInput}
              onInputChange={(event, newInputValue) => setProductInput(newInputValue)}
              getOptionLabel={getOptionLabel}
              isOptionEqualToValue={(option, value) => option.variantId === value.variantId}
              filterSelectedOptions
              noOptionsText="لا توجد منتجات مطابقة"
              loadingText="جارٍ التحميل..."
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.variantId}
                    label={getOptionLabel(option)}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="المنتج / المتغير (يمكن اختيار أكثر من منتج)"
                  placeholder="ابحث باسم المنتج أو الباركود..."
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingProducts ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />
          </Grid>

          {/* نوع السعر - اختياري */}
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              label="نوع السعر"
              value={priceType}
              onChange={(e) => setPriceType(e.target.value as PriceType)}
              fullWidth
              size="small"
              helperText="اختياري: لحساب قيمة المخزون"
            >
              {PRICE_TYPES.map((option) => (
                <MenuItem key={option.value || 'none'} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* الأزرار */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
              >
                مسح
              </Button>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading}
              >
                بحث
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* النتائج */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden' }}>
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 60 }} align="center">#</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 220 }}>المستودع</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }} align="center">الكمية الإجمالية</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 120 }} align="center">عدد المنتجات</TableCell>
                  {priceType && (
                    <TableCell sx={{ fontWeight: 'bold', minWidth: 180 }} align="center">{valueColumnLabel}</TableCell>
                  )}
                  <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }} align="center">تصدير التفاصيل</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {results.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={priceType ? 6 : 5} align="center">
                      <Typography variant="body1" color="textSecondary" sx={{ py: 4 }}>
                        {hasSearched ? 'لا توجد كميات لهذه المنتجات في المستودعات' : 'قم بتحديد تصنيف أو منتج ثم اضغط بحث'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  results.map((row, index) => (
                    <TableRow key={row.warehouseId} hover>
                      <TableCell align="center">{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <InventoryIcon fontSize="small" color="action" />
                          {row.warehouseName}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography sx={{ fontWeight: 'bold', color: row.totalQuantity < 0 ? 'error.main' : 'text.primary' }}>
                          {row.totalQuantity.toLocaleString('en-US')}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">{row.details.length}</TableCell>
                      {priceType && (
                        <TableCell align="center">
                          <Typography sx={{ fontWeight: 'bold' }}>
                            {formatMoney(getWarehouseValue(row, priceType))}
                          </Typography>
                        </TableCell>
                      )}
                      <TableCell align="center">
                        <Tooltip title={hasExportPermission ? 'تصدير تفاصيل هذا المستودع' : 'لا تملك صلاحية التصدير'}>
                          <span>
                            <Button
                              variant="contained"
                              color="success"
                              size="small"
                              startIcon={<FileDownloadIcon />}
                              onClick={() => handleExportWarehouse(row)}
                              disabled={!hasExportPermission}
                            >
                              Excel
                            </Button>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
              {results.length > 0 && (
                <TableFooter>
                  <TableRow>
                    <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>الإجمالي العام</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                      {grandTotal.toLocaleString('en-US')}
                    </TableCell>
                    {priceType ? (
                      <>
                        <TableCell />
                        <TableCell align="center" sx={{ fontWeight: 'bold' }}>
                          {formatMoney(grandValue)}
                        </TableCell>
                        <TableCell />
                      </>
                    ) : (
                      <TableCell colSpan={2} />
                    )}
                  </TableRow>
                </TableFooter>
              )}
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
};

export default CategoryQuantitiesReport;
